import { TryBudgetSetting } from '@/containers/agents/try-budget-setting.component';
import { AppIcon } from '@/components/ui/app-icon';
import { AuthCheck } from '@/components/auth-check.component';
import { Head } from '@/components/head.component';
import { InviteMemberDialog } from '@/components/seats/invite-member-dialog.component';
import { SeatLimitModal } from '@/components/seats/seat-limit-modal.component';
import { SeatUsageDisplay } from '@/components/seats/seat-usage-display.component';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { CreateOrganizationButton } from '@/containers/settings/create-organization-button.component';
import { SettingsLayout } from '@/containers/settings/settings-layout.component';
import {
  SETTINGS_PANE,
  SETTINGS_PANE_HAIRLINE,
  SETTINGS_PANE_ROWS,
  SETTINGS_PANE_SECTION,
  SETTINGS_PANE_SECTION_HEADER,
  SETTINGS_PANE_SECTION_TITLE,
  useInSettingsPane,
} from '@/containers/settings/settings-pane';
import { SettingsPaneHeader } from '@/containers/settings/settings-pane-layout.component';
import { useGetPlansQuery } from '@/store/features/billing.feature';
import { useOrganization, useUser } from '@clerk/react';
import { useIsAdmin } from '@/hooks/use-is-admin.hook';
import { cn } from '@/lib/utils';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import * as React from 'react';
import { toast } from 'sonner';
import {
  iconBuildings,
  iconEllipsis,
  iconEnvelope,
  iconLock,
  iconRightFromBracket,
  iconTrash,
  iconUsers,
} from '@/lib/icons';

type IconShim = React.ComponentType<{ className?: string }>;
const Building2: IconShim = (p) => <AppIcon icon={iconBuildings} {...p} />;
const Lock: IconShim = (p) => <AppIcon icon={iconLock} {...p} />;
const LogOut: IconShim = (p) => <AppIcon icon={iconRightFromBracket} {...p} />;
const Mail: IconShim = (p) => <AppIcon icon={iconEnvelope} {...p} />;
const MoreHorizontal: IconShim = (p) => <AppIcon icon={iconEllipsis} {...p} />;
const Trash2: IconShim = (p) => <AppIcon icon={iconTrash} {...p} />;
const Users: IconShim = (p) => <AppIcon icon={iconUsers} {...p} />;

export const Route = createFileRoute('/settings/team')({
  component: TeamRoute,
});

// Shared card styling, matched to settings/notifications.tsx and the
// agents/icp/custom-ai-columns containers so the team page reads as
// part of the same family of settings surfaces.
const CARD_CLASS =
  'rounded-xl border border-black/8 bg-white dark:border-white/8 dark:bg-[#252525]';
const CARD_HEADER_CLASS =
  'border-b border-black/6 px-6 py-4 dark:border-white/6';
const ROW_DIVIDER_CLASS = 'divide-y divide-black/6 dark:divide-white/6';

function TeamRoute() {
  return (
    <>
      <Head heading='Team' />
      <AuthCheck>
        <SettingsLayout>
          <TeamSettings />
        </SettingsLayout>
      </AuthCheck>
    </>
  );
}

/**
 * Single surface for all org-management actions: the org itself
 * (rename / leave / delete), the member list (role + remove), and
 * pending invitations. Replaces the embedded Clerk OrganizationProfile,
 * which couldn't be hidden cleanly inside our layout.
 */
function TeamSettings() {
  const { organization, memberships, invitations, isLoaded } = useOrganization({
    memberships: { pageSize: 50, keepPreviousData: true },
    invitations: { pageSize: 20, keepPreviousData: true },
  });
  const { currentData: plans } = useGetPlansQuery(
    { organizationId: organization?.id ?? '' },
    { skip: !organization?.id }
  );
  const [inviteOpen, setInviteOpen] = useState(false);
  const [seatOpen, setSeatOpen] = useState(false);
  // useIsAdmin must be called unconditionally, before any early return,
  // to satisfy the rules of hooks (it previously ran below the guards).
  const isAdmin = useIsAdmin();
  // In the settings pane the page starts at the pane's top left, under the
  // pane title, with its sections flat on the pane.
  const inPane = useInSettingsPane();
  const page = inPane ? SETTINGS_PANE : 'mx-auto max-w-3xl space-y-6 px-6 py-8';

  // Called by the invite dialog after Clerk accepts the invite. The
  // SDK's useOrganization paginated resources don't auto-refresh on a
  // local invite call, so we have to nudge them — otherwise the new
  // pending invitation (and its impact on the member-count denominator)
  // only shows up after a hard reload.
  const handleInvited = async () => {
    await Promise.allSettled([
      invitations?.revalidate?.(),
      memberships?.revalidate?.(),
    ]);
  };

  if (!isLoaded) {
    if (inPane) {
      return (
        <div className={page} aria-busy='true'>
          <SettingsPaneHeader title='Organization' hideTitle />
          <div className={SETTINGS_PANE_SECTION}>
            <div className='space-y-3 pt-1'>
              <Skeleton className='h-12 w-full rounded-lg' />
              <Skeleton className='h-9 w-full rounded-lg' />
              <Skeleton className='h-9 w-full rounded-lg' />
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className='mx-auto max-w-3xl px-6 py-16 text-center text-sm text-muted-foreground'>
        Loading team…
      </div>
    );
  }

  if (!organization) {
    if (inPane) {
      return (
        <div className={page}>
          <SettingsPaneHeader title='Organization' hideTitle />
          <p className='text-sm text-muted-foreground'>
            You're not in a workspace yet.
          </p>
        </div>
      );
    }
    return (
      <div className={page}>
        <div className={CARD_CLASS + ' px-6 py-8 text-center'}>
          <p className='text-sm text-muted-foreground'>
            You're not in a workspace yet.
          </p>
        </div>
      </div>
    );
  }

  const currentTierKey = plans?.current_plan?.tier_key;
  // Both paid seat catalogs use the server-confirmed seat purchase flow.
  const canManageSeats =
    !!plans &&
    !plans.unlimited_seats &&
    (plans.current_plan?.pricing_version === 'v4' ||
      plans.is_legacy_pricing === false) &&
    !!currentTierKey &&
    currentTierKey !== 'free';

  // The page header keeps Create organization alone; the seat and invite
  // actions sit with the members they act on.
  const actions = <CreateOrganizationButton />;
  const memberActions = (
    <>
      {isAdmin && canManageSeats ? (
        <Button variant='outline' size='sm' onClick={() => setSeatOpen(true)}>
          Manage seats
        </Button>
      ) : null}
      {isAdmin ? (
        <Button size='sm' onClick={() => setInviteOpen(true)}>
          Invite member
        </Button>
      ) : null}
    </>
  );

  return (
    <div className={page}>
      {inPane ? (
        <SettingsPaneHeader title='Organization' hideTitle>
          {actions}
        </SettingsPaneHeader>
      ) : (
        <div className='flex flex-wrap items-end justify-between gap-3'>
          <div>
            <h1 className='text-lg font-semibold tracking-tight text-foreground'>
              Team
            </h1>
            <p className='mt-1 text-sm text-muted-foreground'>
              Manage your organization, invite teammates, and (on paid plans)
              adjust your seat count.
            </p>
          </div>
          <div className='flex items-center gap-2'>{actions}</div>
        </div>
      )}

      {!isAdmin ? (
        <div
          className={
            inPane
              ? 'flex items-start gap-2 text-sm text-muted-foreground'
              : 'flex items-start gap-2 rounded-xl border border-black/[0.08] bg-muted/40 px-4 py-3 text-sm text-muted-foreground dark:border-white/[0.08]'
          }
        >
          <Lock className='mt-0.5 h-4 w-4 shrink-0' />
          <p>Only an admin can invite members or change roles.</p>
        </div>
      ) : null}

      <OrganizationSection isAdmin={isAdmin} />

      <TryBudgetSetting />

      <MembersList isAdmin={isAdmin} actions={memberActions} />

      {invitations && invitations.data && invitations.data.length > 0 ? (
        <PendingInvitations isAdmin={isAdmin} />
      ) : null}

      <InviteMemberDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onInvited={handleInvited}
      />
      <SeatLimitModal open={seatOpen} onOpenChange={setSeatOpen} />
    </div>
  );
}

function SectionHeader({
  icon: Icon,
  title,
  trailing,
}: {
  icon: typeof Building2;
  title: string;
  trailing?: React.ReactNode;
}) {
  // In the pane a section is flat: its title over its rows, no icon or band.
  const inPane = useInSettingsPane();
  if (inPane) {
    return (
      <div className={SETTINGS_PANE_SECTION_HEADER}>
        <h2 className={SETTINGS_PANE_SECTION_TITLE}>{title}</h2>
        {trailing ?? null}
      </div>
    );
  }
  return (
    <div className={CARD_HEADER_CLASS}>
      <div className='flex items-center justify-between gap-3'>
        <div className='flex items-center gap-2'>
          <Icon className='h-4 w-4 text-muted-foreground' />
          <h2 className='text-sm font-medium text-foreground'>{title}</h2>
        </div>
        {trailing ?? null}
      </div>
    </div>
  );
}

function OrganizationSection({ isAdmin }: { isAdmin: boolean }) {
  const { organization, membership } = useOrganization();
  const { user } = useUser();
  const navigate = useNavigate();
  const inPane = useInSettingsPane();
  const [name, setName] = useState(organization?.name ?? '');
  const [saving, setSaving] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Deleting asks for the organization's name, typed, before it is armed.
  const [confirmName, setConfirmName] = useState('');

  // useState only reads `organization?.name` on first mount, so a
  // workspace switch (Clerk OrganizationSwitcher) leaves the local
  // `name` state pointing at the previous org's name. Sync on change.
  useEffect(() => {
    setName(organization?.name ?? '');
  }, [organization?.id, organization?.name]);

  if (!organization) return null;
  const dirty = name.trim().length > 0 && name.trim() !== organization.name;
  // Leaving is gated server-side by Clerk: the API refuses if this
  // member is the org's last admin (which would orphan the org), but
  // allows it whenever another admin remains regardless of how many
  // other workspaces the user belongs to. If a user leaves their only
  // workspace, `OnboardingCheck`'s `needsOrgRecovery` branch catches
  // the empty-state and prompts them to create a new one — so we don't
  // need a frontend gate for that case either. We previously gated on
  // "user is in >1 workspace", which incorrectly disabled Leave for
  // anyone with a single workspace even when the org had multiple
  // admins. The one case we can see up front is a sole member: Clerk
  // always refuses that (replay: a new signup got a bare "Not found").
  const soleMember = organization.membersCount <= 1;
  const canLeave = Boolean(membership) && !soleMember;
  // Delete is off for customers on our Clerk instance
  // (`admin_delete_enabled: false`), and a click only got Clerk's raw
  // "admin delete not enabled". Show it only where it can succeed.
  const canDelete = isAdmin && organization.adminDeleteEnabled;

  const onSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await organization.update({ name: name.trim() });
      toast.success('Organization name updated.');
    } catch {
      toast.error('Could not update organization name.');
    } finally {
      setSaving(false);
    }
  };

  const onLeave = async () => {
    if (!user || leaving) return;
    setLeaving(true);
    try {
      // The self-leave endpoint; membership.destroy() is the admin
      // remove-member call, which a plain member can't make.
      await user.leaveOrganization(organization.id);
      setLeaveOpen(false);
      toast.success(`You left ${organization.name}.`);
      navigate({ to: '/' });
    } catch {
      // Clerk's own text ("Not found", …) means nothing to a customer.
      // The usual cause is being the last admin.
      toast.error(
        isAdmin
          ? "Couldn't leave. If you're the only admin, make another member an admin first."
          : "Couldn't leave this organization. Please try again."
      );
    } finally {
      setLeaving(false);
    }
  };

  const nameMatches = confirmName.trim() === organization.name;
  const onDelete = async () => {
    if (!nameMatches || deleting) return;
    setDeleting(true);
    try {
      await organization.destroy();
      setDeleteOpen(false);
      toast.success('Organization deleted.');
      navigate({ to: '/' });
    } catch {
      toast.error("Couldn't delete this organization. Please contact support.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section
      className={
        inPane
          ? cn(
              SETTINGS_PANE_SECTION,
              'first-of-type:border-t-0 first-of-type:pt-0'
            )
          : CARD_CLASS
      }
    >
      <SectionHeader
        icon={Building2}
        title={inPane ? 'General' : 'Organization'}
      />

      <div
        className={
          inPane
            ? 'flex items-center gap-4 py-4'
            : 'flex items-center gap-4 px-6 py-5'
        }
      >
        <Avatar className='h-12 w-12'>
          {organization.imageUrl ? (
            <AvatarImage src={organization.imageUrl} alt={organization.name} />
          ) : null}
          <AvatarFallback className='text-sm font-semibold'>
            {(organization.name[0] ?? '?').toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className='min-w-0 flex-1 space-y-1'>
          <label
            htmlFor='org-name'
            className='text-[12px] font-medium text-muted-foreground'
          >
            Name
          </label>
          {isAdmin ? (
            <form
              className='flex items-center gap-2'
              onSubmit={(event) => {
                event.preventDefault();
                void onSave();
              }}
            >
              <Input
                id='org-name'
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={saving}
                className='h-9 max-w-sm'
              />
              <Button type='submit' size='sm' disabled={!dirty || saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </form>
          ) : (
            <p className='text-sm text-foreground'>{organization.name}</p>
          )}
          {organization.slug ? (
            <p className='text-xs text-muted-foreground'>
              @{organization.slug}
            </p>
          ) : null}
        </div>
      </div>

      <div
        className={
          inPane
            ? cn(
                'flex flex-wrap items-center justify-end gap-3 border-t pt-3',
                SETTINGS_PANE_HAIRLINE
              )
            : 'flex flex-wrap items-center justify-end gap-3 border-t border-black/[0.06] bg-muted/20 px-6 py-3 dark:border-white/[0.06]'
        }
      >
        <div className='flex flex-wrap items-center justify-end gap-2'>
          {soleMember ? (
            <p className='text-xs text-muted-foreground'>
              You're the only member, so you can't leave this organization.
            </p>
          ) : null}
          <Button
            type='button'
            variant='outline'
            size='sm'
            disabled={!canLeave}
            onClick={() => setLeaveOpen(true)}
          >
            <LogOut className='mr-1.5 h-3.5 w-3.5' />
            Leave organization
          </Button>
          {canDelete ? (
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='border-red-500/40 text-red-600 hover:bg-red-500/5 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300'
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 className='mr-1.5 h-3.5 w-3.5' />
              Delete organization
            </Button>
          ) : null}
        </div>
      </div>

      <AlertDialog
        open={leaveOpen}
        onOpenChange={(open) => {
          if (!leaving) setLeaveOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave {organization.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll lose access to this workspace's lists, agents, and
              integrations. An admin will need to re-invite you to get back in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={leaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={leaving}
              onClick={(event) => {
                event.preventDefault();
                void onLeave();
              }}
            >
              {leaving ? 'Leaving…' : 'Leave organization'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (deleting) return;
          setDeleteOpen(open);
          if (!open) setConfirmName('');
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {organization.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the organization, all its members, lead
              lists, agents, and billing records. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='space-y-1.5'>
            <label
              htmlFor='org-delete-confirm'
              className='text-[12px] font-medium text-muted-foreground'
            >
              Type {organization.name} to confirm
            </label>
            <Input
              id='org-delete-confirm'
              value={confirmName}
              autoComplete='off'
              disabled={deleting}
              onChange={(event) => setConfirmName(event.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant='destructive'
              disabled={!nameMatches || deleting}
              onClick={(event) => {
                event.preventDefault();
                void onDelete();
              }}
              className='bg-red-600 hover:bg-red-700 focus:ring-red-500'
            >
              {deleting ? 'Deleting…' : 'Delete organization'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function MembersList({
  isAdmin,
  actions,
}: {
  isAdmin: boolean;
  actions: React.ReactNode;
}) {
  const { memberships, organization } = useOrganization({
    memberships: { pageSize: 50, keepPreviousData: true },
  });
  const { user } = useUser();
  const { currentData: plans } = useGetPlansQuery(
    { organizationId: organization?.id ?? '' },
    { skip: !organization?.id }
  );
  const inPane = useInSettingsPane();
  const inset = inPane ? '' : 'px-6';

  if (!memberships) return null;
  const rows = memberships.data ?? [];
  const count = memberships.count ?? rows.length;
  // Show "used / paid" when we know the seat allowance (V2 paid
  // tiers), otherwise just the count. Legacy orgs have no seats
  // concept so the denominator would be misleading.
  const seatsPaid = plans?.seats_paid;
  const showSeatRatio =
    plans &&
    !plans.unlimited_seats &&
    plans.is_legacy_pricing === false &&
    typeof seatsPaid === 'number' &&
    seatsPaid > 0;

  return (
    <section className={inPane ? SETTINGS_PANE_SECTION : CARD_CLASS}>
      <SectionHeader
        icon={Users}
        title='Members'
        trailing={
          <div className='flex flex-wrap items-center gap-2'>
            {showSeatRatio ? (
              <SeatUsageDisplay />
            ) : (
              <span className='text-xs text-muted-foreground'>
                {count} total
              </span>
            )}
            {actions}
          </div>
        }
      />

      <div className={inPane ? SETTINGS_PANE_ROWS : ROW_DIVIDER_CLASS}>
        {rows.length === 0 ? (
          <p
            className={cn(
              'py-8 text-sm text-muted-foreground',
              inPane ? '' : 'px-6 text-center'
            )}
          >
            {memberships.isLoading ? 'Loading members…' : 'No members'}
          </p>
        ) : (
          rows.map((m) => {
            const pub = m.publicUserData;
            const name =
              [pub?.firstName, pub?.lastName].filter(Boolean).join(' ') ||
              pub?.identifier ||
              'Unknown user';
            const isSelf = !!user?.id && pub?.userId === user.id;
            const isMemberAdmin = m.role === 'org:admin';
            const joined = m.createdAt
              ? new Date(m.createdAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })
              : 'Unknown';
            return (
              <div
                key={m.id}
                className={cn('flex items-center gap-4 py-3', inset)}
              >
                <Avatar className='h-8 w-8'>
                  {pub?.imageUrl ? (
                    <AvatarImage src={pub.imageUrl} alt={name} />
                  ) : null}
                  <AvatarFallback className='text-xs'>
                    {(name[0] ?? '?').toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className='min-w-0 flex-1'>
                  <p className='truncate text-sm font-medium text-foreground'>
                    {name}
                    {isSelf ? (
                      <span className='ml-1.5 text-xs font-normal text-muted-foreground'>
                        (you)
                      </span>
                    ) : null}
                  </p>
                  <p className='truncate text-xs text-muted-foreground'>
                    {pub?.identifier ?? ''}
                  </p>
                </div>
                <span className='hidden text-xs whitespace-nowrap text-muted-foreground sm:block'>
                  {joined}
                </span>
                <span
                  className={
                    'rounded-md border px-2 py-0.5 text-xs font-medium ' +
                    (isMemberAdmin
                      ? 'border-[#3E8BFF]/30 bg-[#3E8BFF]/10 text-[#3E8BFF] dark:text-[#7CB1FF]'
                      : 'border-black/8 bg-muted/40 text-muted-foreground dark:border-white/8')
                  }
                >
                  {isMemberAdmin ? 'Admin' : 'Member'}
                </span>
                <div className='w-8 text-right'>
                  {isAdmin && !isSelf ? (
                    <RowMenu
                      memberName={name}
                      onChangeRole={async () => {
                        try {
                          await m.update({
                            role: isMemberAdmin ? 'org:member' : 'org:admin',
                          });
                          toast.success(
                            isMemberAdmin
                              ? `${name} is now a Member.`
                              : `${name} is now an Admin.`
                          );
                          await memberships.revalidate?.();
                        } catch {
                          toast.error('Could not change role.');
                        }
                      }}
                      onRemove={async () => {
                        if (!organization) return;
                        // Guard against publicUserData being absent (Clerk
                        // doesn't guarantee it for every membership shape).
                        // Without this the non-null assertion would throw
                        // a TypeError on `pub!.userId` and the toast
                        // below would still render success.
                        const removeUserId = pub?.userId;
                        if (!removeUserId) {
                          toast.error(
                            'Could not remove member: missing user id.'
                          );
                          return;
                        }
                        try {
                          await organization.removeMember(removeUserId);
                          toast.success(`Removed ${name} from the team.`);
                          await memberships.revalidate?.();
                        } catch {
                          toast.error('Could not remove member.');
                        }
                      }}
                      isAdmin={isMemberAdmin}
                    />
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>

      {memberships.hasNextPage ? (
        <div
          className={cn(
            'flex justify-center border-t py-3',
            SETTINGS_PANE_HAIRLINE,
            inset
          )}
        >
          <Button
            variant='outline'
            size='sm'
            onClick={() => memberships.fetchNext?.()}
            disabled={memberships.isFetching}
          >
            {memberships.isFetching ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

function PendingInvitations({ isAdmin }: { isAdmin: boolean }) {
  const { invitations } = useOrganization({
    invitations: { pageSize: 20, keepPreviousData: true },
  });
  const inPane = useInSettingsPane();
  const rows = invitations?.data ?? [];
  const [invitationToRevoke, setInvitationToRevoke] = useState<
    (typeof rows)[number] | null
  >(null);
  if (!invitations) return null;
  if (rows.length === 0) return null;

  return (
    <section className={inPane ? SETTINGS_PANE_SECTION : CARD_CLASS}>
      <SectionHeader
        icon={Mail}
        title='Pending invitations'
        trailing={
          <span className='text-xs text-muted-foreground'>
            {rows.length} pending
          </span>
        }
      />
      <div className={inPane ? SETTINGS_PANE_ROWS : ROW_DIVIDER_CLASS}>
        {rows.map((inv) => (
          <div
            key={inv.id}
            className={cn(
              'flex items-center justify-between gap-3 py-3',
              inPane ? '' : 'px-6'
            )}
          >
            <div className='min-w-0'>
              <p className='truncate text-sm font-medium text-foreground'>
                {inv.emailAddress}
              </p>
              <p className='text-xs text-muted-foreground'>
                Invited as {inv.role === 'org:admin' ? 'Admin' : 'Member'}
              </p>
            </div>
            {isAdmin ? (
              <Button
                variant='ghost'
                size='sm'
                onClick={() => setInvitationToRevoke(inv)}
              >
                Revoke
              </Button>
            ) : null}
          </div>
        ))}
      </div>
      <AlertDialog
        open={!!invitationToRevoke}
        onOpenChange={(open) => {
          if (!open) setInvitationToRevoke(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke invitation?</AlertDialogTitle>
            <AlertDialogDescription>
              {invitationToRevoke?.emailAddress}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant='destructive'
              onClick={async () => {
                if (!invitationToRevoke) return;
                try {
                  await invitationToRevoke.revoke();
                  toast.success(
                    `Revoked invite to ${invitationToRevoke.emailAddress}.`
                  );
                  await invitations.revalidate?.();
                } catch {
                  toast.error('Could not revoke invite.');
                }
              }}
            >
              Revoke invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function RowMenu({
  memberName,
  onChangeRole,
  onRemove,
  isAdmin,
}: {
  memberName: string;
  onChangeRole: () => void;
  onRemove: () => void;
  isAdmin: boolean;
}) {
  const [removeOpen, setRemoveOpen] = useState(false);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8'
            aria-label='Member actions'
          >
            <MoreHorizontal className='h-4 w-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end' className='w-44'>
          <DropdownMenuItem onClick={onChangeRole}>
            {isAdmin ? 'Demote to Member' : 'Promote to Admin'}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setRemoveOpen(true)}
            className='text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400'
          >
            Remove from team
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AlertDialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from team?</AlertDialogTitle>
            <AlertDialogDescription>{memberName}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant='destructive' onClick={onRemove}>
              Remove from team
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
